<script lang="ts">
    import Truncate from '@amp/web-app-components/src/components/Truncate/Truncate.svelte';
    import { onMount } from 'svelte';
    import type { SanitizeHtmlOptions } from '@amp/web-app-components/src/utils/sanitize-html';

    //===============
    // required prop
    //===============

    /**
     * title of media.
     * @type {string}
     */
    export let title: string;

    //===========
    // functions
    //===========

    /**
     * function passed to Truncate for translations.
     * @type {function}
     */
    export let translateFn: (key: string) => string = null;

    //================
    // optional props
    //================

    /**
     * description text that will be placed in a modal if it's longer than 3 lines.
     * @type {string | null}
     */
    export let modalText: string | null = null;

    /**
     * subtitle text for description modal.
     * @type {string | null}
     */
    export let modalSubtitle: string | null = null;

    /**
     * title for description modal.
     * @type {string | null}
     */
    export let modalTitle: string | null = null;

    /**
     * title to show if a particular media isn't playable in the app
     * @type {string | null}
     */
    export let nonPlayableConfigTitle: string | null = null;

    /**
     * options used when truncating HTML text
     * @type {SanitizeHtmlOptions | undefined}
     */
    export let sanitizeHtmlOptions: SanitizeHtmlOptions | undefined = undefined;

    /**
     * Allow titles and subtitles to render on multiple lines
     * @type {boolean}
     */
    export let useExpandedHeadings: boolean = false;

    //===========
    // local references
    //===========

    let titleHeight: number;
    let subtitleHeight: number;

    //===========
    // constants
    //===========

    const MAX_PARAGRAPH_LINES = 3;
    const SINGLE_LINE_TITLE = 32;
    const TWO_LINE_TITLE_SMALL = 52;
    const TWO_LINE_SUBTITLE = 62;
    const TWO_LINE_SUBTITLE_SMALL = 54;
    const THREE_LINE_SUBTITLE_SMALL = 76;

    $: updateTitleSize =
        (useExpandedHeadings && titleHeight > SINGLE_LINE_TITLE * 2) ||
        titleHeight === TWO_LINE_TITLE_SMALL;
    $: updateSubtitleSize =
        (useExpandedHeadings && titleHeight > SINGLE_LINE_TITLE) ||
        subtitleHeight > TWO_LINE_SUBTITLE ||
        subtitleHeight === TWO_LINE_SUBTITLE_SMALL;
    $: updateSubtitleSpacing =
        useExpandedHeadings &&
        titleHeight === SINGLE_LINE_TITLE &&
        subtitleHeight === THREE_LINE_SUBTITLE_SMALL;

    onMount(() => {
        document
            .querySelector('.primary-actions')
            ?.querySelector('[data-should-focus]')
            ?.querySelector('button')
            ?.focus();
    });
</script>

<!-- <ContainerDetailHeader> -->
<div
    class="container-detail-header"
    class:container-detail-header--no-description={!modalText}
    class:container-detail-header--metadata-tall={useExpandedHeadings}
    data-testid="container-detail-header"
>
    {#if $$slots['artwork']}
        <slot name="artwork" />
    {/if}
    <div class="headings">
        {#if $$slots['metadata-top']}
            <div class="headings__metadata-top">
                <slot name="metadata-top" />
            </div>
        {/if}

        <h1
            class="headings__title"
            class:headings__title--fluid-size={updateTitleSize}
            bind:clientHeight={titleHeight}
            data-testid="non-editable-product-title"
        >
            <span dir="auto">{title}</span
            >{#if $$slots['favorited-badge'] || $$slots['explicit']}
                <span class="headings__badges">
                    {#if $$slots['favorited-badge']}
                        <slot name="favorited-badge" />
                    {/if}
                    {#if $$slots['explicit']}
                        <slot name="explicit" />
                    {/if}
                </span>
            {/if}
        </h1>

        {#if $$slots['subtitles']}
            <div
                class="headings__subtitles"
                data-testid="product-subtitles"
                class:headings__subtitles--fluid-size={updateSubtitleSize}
                class:headings__subtitles--fluid-spacing={updateSubtitleSpacing}
                bind:clientHeight={subtitleHeight}
            >
                <slot name="subtitles" />
            </div>
        {/if}

        {#if $$slots['tertiary-titles']}
            <div class="headings__tertiary-titles">
                <slot name="tertiary-titles" />
            </div>
        {/if}

        {#if $$slots['metadata-bottom']}
            <div class="headings__metadata-bottom">
                <slot name="metadata-bottom" />
            </div>
        {/if}
    </div>

    {#if modalText}
        <div class="description" data-testid="description">
            <Truncate
                text={modalText}
                lines={MAX_PARAGRAPH_LINES}
                title={modalTitle}
                subtitle={modalSubtitle}
                modalType="contentModal"
                {sanitizeHtmlOptions}
                {translateFn}
            />
        </div>
    {/if}

    <div class="primary-actions">
        {#if nonPlayableConfigTitle}
            <div class="primary-actions__siri-banner" data-testid="siri-banner">
                {nonPlayableConfigTitle}
            </div>
        {:else if $$slots['play-button'] || $$slots['shuffle-button']}
            {#if $$slots['play-button']}
                <div
                    class="primary-actions__button primary-actions__button--play"
                >
                    <slot name="play-button" />
                </div>
            {/if}

            {#if $$slots['shuffle-button']}
                <div
                    class="primary-actions__button primary-actions__button--shuffle"
                >
                    <slot name="shuffle-button" />
                </div>
            {/if}
        {/if}
    </div>

    {#if $$slots['secondary-actions']}
        <div class="secondary-actions">
            <slot name="secondary-actions" />
        </div>
    {/if}
</div>

<!-- </ContainerDetailHeader> -->
<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/radiosity-effect' as *;
    @use '@amp/web-shared-styles/app/core/mixins/keycolor-button-states' as *;

    $artwork-size: 270px;

    // Default system focus-ring offset
    $focus-offset: 1px;

    .container-detail-header {
        margin: 0 var(--bodyGutter);
        margin-bottom: var(--containerDetailHeaderSpacer, #{$spacerC});
        padding-top: 20px; // Factor top spacing into full container height
        display: grid;
        grid-template-areas:
            'secondary-actions'
            'artwork'
            'headings'
            'primary-actions'
            'description';
        justify-items: center;

        @media (--small) {
            margin-bottom: var(--containerDetailHeaderSpacer, 40px);
            padding-top: 40px; // Factor top spacing into full container height
            grid-template-areas:
                'artwork    headings            headings'
                'artwork    description         description'
                'artwork    primary-actions     secondary-actions';

            // Headings take up remaining free space
            grid-template-rows: 1fr auto auto;

            // Primary action buttons take up remaining free space
            grid-template-columns: auto 1fr auto;
            justify-items: start;
        }
    }

    .container-detail-header--metadata-tall {
        .headings__title {
            @include line-clamp(9);
        }

        .headings__subtitles {
            font: var(--title-1);
            white-space: normal;

            @include line-clamp(3);
        }

        .headings__subtitles--fluid-size {
            font: var(--title-2);
        }

        .headings__subtitles--fluid-spacing {
            margin-top: 6px;
        }

        .headings__metadata-bottom {
            margin-top: 6px;
        }

        .description {
            margin-top: 14px;
        }
    }

    .headings {
        min-width: 0; // prevent grid blowout in xsmall viewports
        width: 100%;
        margin-top: 18px;
        grid-area: headings;
        display: flex;
        flex-direction: column;
        align-items: center;

        @media (--small) {
            margin-top: 0;
            align-items: flex-start;
            align-self: end;

            @include overflow-bleed-top($focus-size + $focus-offset);
            @include overflow-bleed-bottom($focus-size + $focus-offset);

            .container-detail-header--no-description & {
                align-self: center;
            }
        }
    }

    .headings__title {
        margin-bottom: -2px;
        color: var(--systemPrimary);
        user-select: text;
        cursor: text;
        word-break: break-word; // Avoid overflow of very long strings
        text-align: center;
        font: var(--large-title-semibold);

        @include line-clamp(2);

        @media (--small) {
            text-align: unset;
        }
    }

    .headings__badges {
        --explicitBadgeSize: 19px;
        --favoriteBadgeSize: 14px;
        --favoriteBadgeMarginInlineStart: 1px;
        margin-inline-start: 4px;
        display: inline-flex;
        align-items: center;
        gap: 6px;

        // Nudge favorite badge to vertical center
        :global(.favorite svg) {
            vertical-align: 2px;
        }
    }

    .headings__subtitles {
        --linkColor: var(--keyColor);
        width: 100%;
        color: var(--keyColor);
        text-align: center;
        font: var(--large-title);

        @include line-clamp;
        @include overflow-bleed($focus-size + $focus-offset);

        @media (--small) {
            text-align: unset;
        }
    }

    .headings__metadata-top {
        /* TODO: rdar://77832318: should be 9px, but ratings icon pushes this down */
        margin-top: 9px;
        color: var(--systemSecondary);
        font: var(--subhead-emphasized);

        :global(svg) {
            fill: var(--systemSecondary);
        }

        &:empty {
            display: none;
        }
    }

    .headings__metadata-bottom {
        margin-top: 4px;
        color: var(--systemSecondary);
        text-align: center;
        font: var(--callout-emphasized);

        &:empty {
            display: none;
        }
    }

    // in some cases like music, we need a draggable div around the slot.
    // this will cause some issues with nesting styles and there's currently no way to add a class to a slot
    // so instead, we'll globally select by slot name.

    // when implementing this component, do not use a svelte fragment for Artwork or else this will not work.
    :global([slot='artwork']) {
        --radiosity-effect-shadow-z: var(--z-gpu);
        grid-area: artwork;
        width: var(--artworkSize, #{$artwork-size});
        margin: 15px auto 0;
        align-self: start; // Keeps radiosity/shadow from expanding grid when contents overflow
        border-radius: var(
            --global-border-radius-large,
            #{$global-border-radius-large}
        );

        @include radiosity-effect-shadow;

        @media (--small) {
            margin-top: 0;
            margin-inline-end: 34px;
        }
    }

    .description {
        --truncate-font: var(--body-tall); // font is set in Truncate component:
        grid-area: description;
        margin-top: 18px;
        max-width: 440px;
        min-height: 54px; // ensure correct spacing even when description is less than three lines
        color: var(--systemSecondary);

        @media (--small) {
            margin-top: 28px;
            align-self: end;
        }
    }

    .primary-actions {
        grid-area: primary-actions;
        margin-top: 18px;
        display: flex;
        justify-content: center;
        gap: 16px;

        // Allow wrapping. Some locales can have wide action/iTunes buttons that collide.
        flex-wrap: wrap;
        row-gap: $spacerA;

        @media (--small) {
            margin-top: 36px;
            align-self: end;
            justify-content: flex-start;
            gap: 10px;

            // Remove margin when there's no description so titles will vertically center
            .container-detail-header--no-description & {
                margin-top: 0;
            }
        }
    }

    .primary-actions__siri-banner {
        margin-top: auto;
        height: 48px;
        max-width: 440px;
        display: flex;
        align-items: center;
        font: var(--title-3-emphasized);

        &::before {
            width: 41px;
            height: 41px;
            content: '';
            display: inline-block;
            background-size: contain;
            flex-shrink: 0; // Maintain artwork size
            background-repeat: no-repeat;
            background-image: url('/assets/icons/siri_logo_light.png');
            margin-inline-start: -3px; // Counteract artwork padding
            margin-inline-end: 6px;

            @media (prefers-color-scheme: dark) {
                background-image: url('/assets/icons/siri_logo_dark.png');
            }
        }
    }

    .primary-actions__button {
        :global(.button button) {
            min-width: 127px;
            height: 28px;
            padding: 0 12px;
            border-radius: 6px;

            @include keycolor-button-states;

            @media (--small) {
                min-width: 100px;
            }
        }

        :global(.icon) {
            display: inline-block;
            width: 16px;
            vertical-align: baseline;
        }

        &:empty {
            display: none;
        }
    }

    .primary-actions__button--shuffle :global(.icon) {
        margin-inline-end: 4px;
    }

    .primary-actions__button--play :global(.icon) {
        margin-bottom: 2px; //nudge play icon up
        margin-inline-end: 2px;
    }

    .secondary-actions {
        grid-area: secondary-actions;
        justify-self: end;
        display: flex;
        gap: 10px;

        @media (--small) {
            align-self: end;
        }
    }
</style>
